import { Menu, Transition } from "@headlessui/react"
import * as React from "react"
import { FC, Fragment, useEffect, useState } from "react"
import { KSpan } from "kahuna-base-react-components"
//@ts-ignore
import { useNavigate } from "react-router-dom"
import { users } from "../../../../../apis"
import { toast } from "react-toastify"
import { ADMIN_PERMISSIONS, errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
import { UserTakedown } from "../../../../../types"
import { checkAdminPermission } from "../../../../../utility"

export type AdminUserTakedownMenuProps = {
  selectedTakedown: UserTakedown
  fetchUserTakedownList: () => void
  onTakedownCatalogMetadata: (selectedTakedown: UserTakedown) => void
  onTakedownCatalogAudioAsset: (selectedTakedown: UserTakedown) => void
  userAdminPermissions?: string
}

const AdminUserTakedownMenu: FC<AdminUserTakedownMenuProps> = (props) => {
  const [userId, setUserId] = useState<number>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setUserId(props.selectedTakedown?.userId)
  }, [props.selectedTakedown])

  const onAuthenticateLabelCamp = () => {
    setLoading(true)
    users(`/user_takedown_authenticate_lc/`, "PUT", {
      user_id: props.selectedTakedown?.userId,
      id: props.selectedTakedown?.id
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
        return
      }
      toast.success(`Successfully verified and authenticated labelcamp`, successFilledToastOptions)
      props.fetchUserTakedownList()
      setLoading(false)
    })
  }

  const onSignContract = () => {
    if (!window.confirm("Sign Contract. Are you sure?")) return
    setLoading(true)
    users(`/user_takedown_sign_contract/`, "PUT", {
      user_id: props.selectedTakedown?.userId,
      id: props.selectedTakedown?.id
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
        return
      }
      toast.success(`Successfully signed contract`, successFilledToastOptions)
      props.fetchUserTakedownList()
      setLoading(false)
    })
  }

  const onCreateLabel = () => {
    if (!window.confirm("Create Label inside Labelcamp. Are you sure?")) return
    setLoading(true)
    users(`/user_takedown_create_label/`, "PUT", {
      user_id: props.selectedTakedown?.userId,
      id: props.selectedTakedown?.id
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
        return
      }
      toast.success(`Successfully created label`, successFilledToastOptions)
      props.fetchUserTakedownList()
      setLoading(false)
    })
  }

  return (
    <Menu as="div" className="ml relative">
      <div>
        <Menu.Button data-testid="admin-user-menu-button" className={"focus:outline-none"}>
          <div className="w-9 h-9 p-2 rounded-[10px] flex items-center justify-center bg-[#F7F7F7] hover:bg-[#F3F3F3]">
            <img
              className="min-w-5 min-h-5"
              src={"/admin_icons/horizontal-dots.svg"}
              width={20}
              height={20}
              alt="dots-icon"
            />
          </div>
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={`z-20 origin-top-right absolute ${"right-0"} mt-2 p-2 rounded-md shadow-lg py-1 bg-[#F7F7F7] ring-1
          ring-black ring-opacity-5 focus:outline-none flex flex-col divide-y divide-gray-200`}
        >
          <div className="flex flex-col px-1 py-1 gap-1 z-20 w-[260px]">
            <Menu.Item>
              {({ active }) => (
                <div
                  className="flex flex-row gap-2 items-center w-full py-2.5 px-2 bg-[#F7F7F7] hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                  onClick={() => onSignContract()}
                >
                  <KSpan text="Sign Contract" color="#000" />
                </div>
              )}
            </Menu.Item>
            {checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.userAdminPermissions) && (
              <Menu.Item>
                {({ active }) => (
                  <div
                    className="flex flex-row gap-2 items-center w-full py-2.5 px-2 bg-[#F7F7F7] hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                    onClick={() => onCreateLabel()}
                  >
                    <KSpan text="Create Labelcamp Label" color="#000" />
                  </div>
                )}
              </Menu.Item>
            )}
            <Menu.Item>
              {({ active }) => (
                <div
                  className="flex flex-row gap-2 items-center w-full py-2.5 px-2 bg-[#F7F7F7] hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                  onClick={() => onAuthenticateLabelCamp()}
                >
                  <KSpan text="LabelCamp Authenticate" color="#000" />
                </div>
              )}
            </Menu.Item>
            {checkAdminPermission(ADMIN_PERMISSIONS.USER_TAKEDOWN_TAKEDOWN_METADATA, props.userAdminPermissions) && (
              <Menu.Item disabled={!props.selectedTakedown?.authenticatedLc}>
                {({ active }) => (
                  <div
                    className="flex flex-row gap-2 items-center w-full mt-2 py-3.5 px-2 bg-[#F7F7F7] hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                    onClick={() => props.onTakedownCatalogMetadata(props.selectedTakedown)}
                    style={{ borderTop: "1px dashed grey" }}
                  >
                    <KSpan
                      text="Takedown Catalog Metadata"
                      color={props.selectedTakedown?.authenticatedLc ? "#000" : "#00000032"}
                    />
                  </div>
                )}
              </Menu.Item>
            )}

            {checkAdminPermission(ADMIN_PERMISSIONS.USER_TAKEDOWN_UPLOAD_AUDIO_ASSET, props.userAdminPermissions) && (
              <Menu.Item disabled={props.selectedTakedown?.takedownMetadataStatus !== "success"}>
                {({ active }) => (
                  <div
                    className="flex flex-row gap-2 items-center w-full pb-3 pt-1 px-2 bg-[#F7F7F7] hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                    onClick={() => props.onTakedownCatalogAudioAsset(props.selectedTakedown)}
                    style={{ borderBottom: "1px dashed grey" }}
                  >
                    <KSpan
                      text="Upload Audio Assets From Drive"
                      color={props.selectedTakedown?.takedownMetadataStatus === "success" ? "#000" : "#00000032"}
                    />
                  </div>
                )}
              </Menu.Item>
            )}

            <Menu.Item>
              {({ active }) => (
                <div
                  className="flex flex-row gap-2 items-center w-full py-3 px-2 bg-[#F7F7F7] hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                  onClick={() => {
                    toast.success("Link copied to clipboard")
                    navigator.clipboard.writeText(`${process.env.REACT_APP_FRONT_END_URL}/?release_submission=1`)
                  }}
                >
                  <KSpan text="Copy Release Submission Link" color={"#000"} />
                </div>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <div
                  className="flex flex-row gap-2 items-center w-full py-3 px-2 bg-[#F7F7F7] hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                  onClick={() => {
                    toast.success("Link copied to clipboard")
                    navigator.clipboard.writeText(`${process.env.REACT_APP_FRONT_END_URL}/?takedown_overview=1`)
                  }}
                >
                  <KSpan text="Copy Takedown Overview Link" color={"#000"} />
                </div>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default AdminUserTakedownMenu
